import { ITranslation, formatDecimal, formatInteger } from "@shared";

import { DutyScheduleTableColumn } from "@/pages/duty-schedule/pages/duty-schedule-detail/components/table-layout/DutyScheduleTableLayout";
import { COUNSELOR_COLUMN_WIDTH } from "@/pages/duty-schedule/pages/duty-schedule-detail/components/table-layout/DutyScheduleTableLayout.styles";
import {
  DutyScheduleDistributionRow,
  TOTALS_KEY,
} from "@/pages/duty-schedule/pages/duty-schedule-detail/pages/distribution/DutyScheduleDistribution.utils";

import { DATA_COLUMN_WIDTH } from "./DutyScheduleDistributionTable.styles";

export function distributionColumnLabel(row: DutyScheduleDistributionRow, t: ITranslation): string {
  if (row.key === TOTALS_KEY) {
    return t("dutySchedule.detail.distribution.table.totalsRow");
  }
  return row.counselor == null
    ? row.userCode
    : t("dutySchedule.detail.distribution.table.counselorLabel", {
        userCode: row.userCode,
        lastName: row.counselor.lastName,
        firstName: row.counselor.firstName,
      });
}

function renderCellValue(value: number, hideZero: boolean, language: string): string {
  if (hideZero && value === 0) {
    return "";
  }
  return formatInteger(Math.round(value), language);
}

// weeklyHours is Counselor.WeeklyHours, a decimal (part-time counselors commonly have fractional
// values like 37.5) — must not be rounded to a whole number like the duty-count columns.
function renderWeeklyHours(value: number, language: string): string {
  return formatDecimal(value, language);
}

export function buildCounselorColumn(t: ITranslation): DutyScheduleTableColumn<DutyScheduleDistributionRow> {
  return {
    field: "counselor",
    headerName: t("dutySchedule.detail.distribution.table.counselorColumn"),
    width: COUNSELOR_COLUMN_WIDTH,
    bold: true,
    getHighlight: (row) => (row.key === TOTALS_KEY ? "totals" : "none"),
    renderCell: (row) => distributionColumnLabel(row, t),
  };
}

export function buildNumericColumns(
  t: ITranslation,
  language: string
): Array<DutyScheduleTableColumn<DutyScheduleDistributionRow>> {
  function numericColumn(
    field: "weeklyHours" | "aw12" | "aw3" | "aw123" | "bgs",
    headerName: string,
    render: (value: number) => string
  ): DutyScheduleTableColumn<DutyScheduleDistributionRow> {
    return {
      field,
      headerName,
      width: DATA_COLUMN_WIDTH,
      align: "right",
      bordered: true,
      getHighlight: (row) => (row.key === TOTALS_KEY ? "totals" : "none"),
      renderCell: (row) => render(row[field]),
    };
  }

  return [
    numericColumn("weeklyHours", t("dutySchedule.detail.distribution.table.weeklyHours"), (value) =>
      renderWeeklyHours(value, language)
    ),
    numericColumn("aw12", t("dutySchedule.detail.distribution.table.aw12"), (value) =>
      renderCellValue(value, true, language)
    ),
    numericColumn("aw3", t("dutySchedule.detail.distribution.table.aw3"), (value) =>
      renderCellValue(value, true, language)
    ),
    numericColumn("aw123", t("dutySchedule.detail.distribution.table.aw123"), (value) =>
      renderCellValue(value, true, language)
    ),
    numericColumn("bgs", t("dutySchedule.detail.distribution.table.bgs"), (value) =>
      renderCellValue(value, true, language)
    ),
  ];
}
